import React, { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Mail, Mailbox, Target, TrendingUp, Users, Workflow, ListChecks, Loader2 } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import { Sidebar } from '@/components/ui/sidebar'
import Header from '@/components/ui/header'
import BottomNav from '@/components/ui/bottom-nav'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet'
import { getMyWorkspaces, type WorkspaceSummary } from '@/features/onboarding/services/workspaceApi'
import CampaignListSection from '@/features/campaigns/components/CampaignListSection'
import CampaignBuilderForm from '@/features/campaigns/components/CampaignBuilderForm'
import MailboxesPanel from '@/features/mailboxes/components/MailboxesPanel'
import { IcpsSection } from '@/features/icps'
import ProspectsSection from '@/features/prospecting/components/ProspectsSection'
import AutomationSettingsSection from '@/features/settings/components/AutomationSettingsSection'
import ApprovalsSettingsSection from '@/features/settings/components/ApprovalsSettingsSection'
import { useApprovalQueue } from '@/features/settings/hooks/useApprovalQueue'
import { SettingsNav, type NavNode } from '@/features/settings/components/SettingsNav'
import type { Campaign } from '@/features/campaigns/types'

const sections = [
  {
    id: 'prospects',
    label: 'Prospects',
    icon: Target,
    desc: 'Companies Zeph found that match your primary ideal customer profile, waiting for your review.',
  },
  {
    id: 'icps',
    label: 'ICPs',
    icon: Users,
    desc: 'The customer segments you sell to — Zeph targets, qualifies and writes to them.',
  },
  {
    id: 'campaigns',
    label: 'Campaigns',
    icon: Mail,
    desc: 'Multi-step outreach sequences Zeph sends on your behalf.',
  },
  {
    id: 'mailboxes',
    label: 'Mailboxes',
    icon: Mailbox,
    desc: 'The accounts Zeph can send campaign email from.',
  },
  {
    id: 'automation',
    label: 'Automation',
    icon: Workflow,
    desc: 'Control what Zeph is allowed to do on its own, action by action.',
  },
  {
    id: 'approvals',
    label: 'Approvals',
    icon: ListChecks,
    desc: 'AI-drafted follow-ups and replies waiting for a decision.',
  },
]

const sectionsById = Object.fromEntries(sections.map((s) => [s.id, s]))

// Every role sees every tab — campaign actions and automation modes are
// permission-gated inside their own sections, the approval queue is one any
// member can act on, ICPs are read-only for members (owners and admins manage them),
// and Prospects hides its Owner/Admin-only usage view itself, so nothing is hidden
// from the nav.
const navNodes: NavNode[] = sections.map((s) => ({ type: 'leaf', id: s.id }))

function tabFromSearch(search: string): string {
  const tab = new URLSearchParams(search).get('tab')
  return sections.some((s) => s.id === tab) ? tab! : 'campaigns'
}

export const SalesPage: React.FC = () => {
  const { currentTheme: t } = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const [active, setActive] = useState(() => tabFromSearch(window.location.search))
  const [workspace, setWorkspace] = useState<WorkspaceSummary | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const activeSection = sectionsById[active] ?? sections[0]!
  // ?view=usage opens Prospects on Team usage (Owner/Admin; ignored for members).
  const prospectsView = new URLSearchParams(location.search).get('view') === 'usage' ? 'usage' : 'review'
  // Held here (not just inside ApprovalsSettingsSection) so the sub-sidebar badge is
  // visible regardless of which tab is active — guarded against an empty
  // workspaceId internally, so this is safe to call before workspace resolves.
  const { items: pendingApprovalItems } = useApprovalQueue(workspace?.id ?? '')
  const navBadges = { approvals: pendingApprovalItems.length }

  useEffect(() => {
    getMyWorkspaces().then((ws) => { if (ws[0]) setWorkspace(ws[0]) }).catch(() => {})
  }, [])

  useEffect(() => {
    setActive(tabFromSearch(location.search))
  }, [location.search])

  const handleTabClick = (id: string) => {
    navigate(`/dashboard/sales?tab=${id}`, { replace: true })
  }

  const handleCreated = (campaign: Campaign) => {
    setCreateOpen(false)
    navigate(`/dashboard/campaigns/${campaign.id}`)
  }

  const loader = <div className="flex justify-center py-16"><Loader2 size={20} className="animate-spin" style={{ color: t.mutedText }} /></div>

  return (
    <div className="h-full flex flex-col overflow-hidden transition-colors duration-300" style={{ backgroundColor: t.primary }}>
      <div className="flex flex-1 min-h-0 overflow-hidden pb-16 md:pb-0">
        <Sidebar />
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          <Header title="Sales" icon={<TrendingUp size={20} />} />

          <main className="flex-1 flex min-h-0 overflow-hidden">
            <aside
              className="hidden md:flex md:flex-col md:w-72 md:shrink-0 gap-1.5 p-4 pr-5 overflow-y-auto custom-scrollbar [scrollbar-gutter:stable]"
              style={{ backgroundColor: t.secondary }}
            >
              <SettingsNav nodes={navNodes} sectionsById={sectionsById} active={active} onSelect={handleTabClick} badges={navBadges} />
            </aside>

            <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar px-4 sm:px-6 md:px-10 py-6 md:py-8 [scrollbar-gutter:stable]">
              <div className="max-w-4xl mx-auto">
                {/* Nav shown inline on mobile, where the persistent aside is hidden */}
                <div className="md:hidden mb-6">
                  <SettingsNav nodes={navNodes} sectionsById={sectionsById} active={active} onSelect={handleTabClick} badges={navBadges} />
                </div>

                <div className="mb-6">
                  <h1 className="text-2xl font-bold" style={{ color: t.text }}>{activeSection.label}</h1>
                  <p className="text-sm mt-1" style={{ color: t.mutedText }}>{activeSection.desc}</p>
                </div>

                {active === 'prospects' && (
                  workspace
                    ? <ProspectsSection workspaceId={workspace.id} role={workspace.role} initialView={prospectsView} />
                    : loader
                )}

                {active === 'icps' && (
                  workspace
                    ? <IcpsSection workspaceId={workspace.id} role={workspace.role} />
                    : loader
                )}

                {/* The campaign list is already a set of bordered rows, so it sits directly on the page rather than inside the card */}
                {active === 'campaigns' && (
                  workspace
                    ? <CampaignListSection workspaceId={workspace.id} onCreate={() => setCreateOpen(true)} />
                    : loader
                )}

                {active === 'mailboxes' && (
                  workspace
                    ? <MailboxesPanel workspaceId={workspace.id} />
                    : loader
                )}

                {active === 'automation' && (
                  <div className="rounded-2xl p-4 sm:p-7" style={{ backgroundColor: t.secondary, border: `1px solid ${t.border}` }}>
                    {workspace
                      ? <AutomationSettingsSection workspaceId={workspace.id} role={workspace.role} />
                      : loader}
                  </div>
                )}

                {active === 'approvals' && (
                  <div className="rounded-2xl p-4 sm:p-7" style={{ backgroundColor: t.secondary, border: `1px solid ${t.border}` }}>
                    {workspace
                      ? <ApprovalsSettingsSection workspaceId={workspace.id} />
                      : loader}
                  </div>
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
      <BottomNav />

      <Sheet open={createOpen} onOpenChange={setCreateOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Create Campaign</SheetTitle>
            <SheetDescription>
              Needs approval before it starts sending.
            </SheetDescription>
          </SheetHeader>
          {workspace && <CampaignBuilderForm workspaceId={workspace.id} onCreated={handleCreated} onCancel={() => setCreateOpen(false)} />}
        </SheetContent>
      </Sheet>
    </div>
  )
}

export default SalesPage
